import { useState } from 'react'
import { Plus, Search, Filter, Eye, Edit, Trash2, CheckCircle, XCircle } from 'lucide-react'
import { Button, Badge, Card, CardBody, Modal } from '../../components/ui'
import type { Creative } from '../../types'

// Mock data
const mockCreatives: Creative[] = [
  {
    id: 'cr-1',
    campaignId: 'camp-1',
    name: 'メインバナー_春ver1',
    creativeType: 'image',
    fileUrl: 'https://placehold.co/728x280/3b82f6/ffffff?text=Spring+Campaign',
    clickUrl: 'https://example.com/game',
    width: 728,
    height: 280,
    altText: '春のゲーム祭り',
    status: 'approved',
    createdAt: '2025-11-20',
    updatedAt: '2025-11-22',
  },
  {
    id: 'cr-2',
    campaignId: 'camp-1',
    name: '正方形バナー_春ver1',
    creativeType: 'image',
    fileUrl: 'https://placehold.co/300x250/10b981/ffffff?text=Game+Paradise',
    clickUrl: 'https://example.com/game',
    width: 300,
    height: 250,
    altText: '春のゲーム祭り',
    status: 'pending',
    createdAt: '2025-11-20',
    updatedAt: '2025-11-20',
  },
  {
    id: 'cr-3',
    campaignId: 'camp-2',
    name: 'メインバナー_新作',
    creativeType: 'image',
    fileUrl: 'https://placehold.co/728x280/f97316/ffffff?text=New+Release',
    clickUrl: 'https://example.com/new',
    width: 728,
    height: 280,
    altText: '新作タイトル',
    status: 'approved',
    createdAt: '2025-11-25',
    updatedAt: '2025-11-26',
  },
  {
    id: 'cr-4',
    campaignId: 'camp-3',
    name: 'カフェバナー',
    creativeType: 'image',
    fileUrl: 'https://placehold.co/728x280/8b5cf6/ffffff?text=Morning+Coffee',
    clickUrl: 'https://example.com/cafe',
    width: 728,
    height: 280,
    altText: 'モーニングコーヒー',
    status: 'rejected',
    rejectionReason: '画像解像度が低すぎます',
    createdAt: '2025-11-28',
    updatedAt: '2025-11-29',
  },
]

const statusConfig = {
  pending: { label: '審査中', variant: 'warning' as const },
  approved: { label: '承認済', variant: 'success' as const },
  rejected: { label: '却下', variant: 'danger' as const },
}

export default function CreativeListPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [previewCreative, setPreviewCreative] = useState<Creative | null>(null)

  const filteredCreatives = mockCreatives.filter(creative => {
    const matchesSearch = creative.name.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesStatus = statusFilter === 'all' || creative.status === statusFilter
    return matchesSearch && matchesStatus
  })

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">クリエイティブ管理</h1>
          <p className="text-sm text-gray-500 mt-1">広告素材のアップロード・審査管理</p>
        </div>
        <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => setShowUploadModal(true)}>
          新規アップロード
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <div className="p-4 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="クリエイティブ名で検索..."
              className="form-input pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex gap-3">
            <select 
              className="form-select w-40"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">すべてのステータス</option>
              <option value="pending">審査中</option>
              <option value="approved">承認済</option>
              <option value="rejected">却下</option>
            </select>
            <Button variant="secondary" leftIcon={<Filter className="w-4 h-4" />}>
              詳細フィルタ
            </Button>
          </div>
        </div>
      </Card>

      {/* Creative Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredCreatives.map((creative) => {
          const status = statusConfig[creative.status]
          return (
            <Card key={creative.id} className="overflow-hidden">
              <div className="aspect-video bg-gray-100 relative">
                <img 
                  src={creative.fileUrl} 
                  alt={creative.altText || creative.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-2 right-2">
                  <Badge variant={status.variant}>{status.label}</Badge>
                </div>
              </div>
              <CardBody>
                <h3 className="font-medium text-gray-900 truncate">{creative.name}</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {creative.width} x {creative.height} px | {creative.creativeType}
                </p>
                {creative.status === 'rejected' && creative.rejectionReason && (
                  <p className="text-sm text-red-600 mt-2 bg-red-50 p-2 rounded">
                    却下理由: {creative.rejectionReason}
                  </p>
                )}
                <div className="mt-4 flex gap-2">
                  <Button 
                    size="sm" 
                    variant="secondary" 
                    leftIcon={<Eye className="w-3 h-3" />}
                    onClick={() => setPreviewCreative(creative)}
                  >
                    プレビュー
                  </Button>
                  <Button size="sm" variant="secondary" leftIcon={<Edit className="w-3 h-3" />}>
                    編集
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardBody>
            </Card>
          )
        })}
      </div>

      {/* Upload Modal */}
      <Modal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        title="クリエイティブをアップロード"
        size="lg"
      >
        <form className="space-y-4">
          <div>
            <label className="form-label">キャンペーン</label>
            <select className="form-select">
              <option value="">キャンペーンを選択</option>
              <option value="camp-1">春のゲーム祭り</option>
              <option value="camp-2">新作タイトルプロモ</option>
              <option value="camp-3">カフェキャンペーン</option>
            </select>
          </div>
          <div>
            <label className="form-label">広告枠</label>
            <select className="form-select">
              <option value="">広告枠を選択</option>
              <option value="main">メインバナー (728x280)</option>
              <option value="square">正方形バナー (300x250)</option>
            </select>
          </div>
          <div>
            <label className="form-label">クリエイティブ名</label>
            <input type="text" className="form-input" placeholder="クリエイティブ名を入力" />
          </div>
          <div>
            <label className="form-label">ファイル</label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary-400 transition-colors cursor-pointer">
              <div className="text-gray-500">
                <p className="font-medium">ファイルをドラッグ＆ドロップ</p>
                <p className="text-sm mt-1">または クリックしてファイルを選択</p>
                <p className="text-xs mt-2 text-gray-400">対応形式: JPG, PNG, GIF (最大2MB)</p>
              </div>
            </div>
          </div>
          <div>
            <label className="form-label">クリックURL</label>
            <input type="url" className="form-input" placeholder="https://example.com/landing" />
          </div>
          <div>
            <label className="form-label">代替テキスト</label>
            <input type="text" className="form-input" placeholder="画像の説明（任意）" />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setShowUploadModal(false)}>
              キャンセル
            </Button>
            <Button type="submit">アップロード</Button>
          </div>
        </form>
      </Modal>

      {/* Preview Modal */}
      <Modal
        isOpen={!!previewCreative}
        onClose={() => setPreviewCreative(null)}
        title={previewCreative?.name || 'プレビュー'}
        size="xl"
      >
        {previewCreative && (
          <div className="space-y-4">
            <div className="bg-gray-100 rounded-lg p-4 flex items-center justify-center">
              <img 
                src={previewCreative.fileUrl} 
                alt={previewCreative.altText || previewCreative.name}
                className="max-w-full h-auto"
              />
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">サイズ:</span>
                <span className="ml-2">{previewCreative.width} x {previewCreative.height} px</span>
              </div>
              <div>
                <span className="text-gray-500">形式:</span>
                <span className="ml-2">{previewCreative.creativeType}</span>
              </div>
              <div>
                <span className="text-gray-500">クリックURL:</span>
                <a href={previewCreative.clickUrl} target="_blank" rel="noopener noreferrer" className="ml-2 text-primary-600 hover:underline">
                  {previewCreative.clickUrl}
                </a>
              </div>
              <div>
                <span className="text-gray-500">ステータス:</span>
                <span className="ml-2">
                  <Badge variant={statusConfig[previewCreative.status].variant}>
                    {statusConfig[previewCreative.status].label}
                  </Badge>
                </span>
              </div>
            </div>
            {previewCreative.status === 'pending' && (
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="danger" leftIcon={<XCircle className="w-4 h-4" />}>
                  却下
                </Button>
                <Button variant="success" leftIcon={<CheckCircle className="w-4 h-4" />}>
                  承認
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}

